import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { TextInput } from "@/components/design/TextField";
import { contrastRatio } from "@/lib/color";
import { deriveRoleTokens, normalizePalette } from "@/lib/theme-tokens";
import { useThemePrefs } from "@/stores/theme-prefs";

// A small slice of the app built from the real primitives and roles, so it shows
// what the derivation does with the palette being edited: the palette applies to
// :root live, and every surface here binds its role like anywhere else. The hint
// below explains the one result that surprises people, the button colour on cards.
export function PalettePreview() {
  const intl = useIntl();
  const page = useThemePrefs((s) => s.bgColor);
  const surface = useThemePrefs((s) => s.surfaceColor);
  const accent = useThemePrefs((s) => s.accentColor);
  const palette = normalizePalette({ page, surface, accent });
  const roles = deriveRoleTokens(palette);
  const t = (id: string, defaultMessage: string, values?: Record<string, string>) =>
    intl.formatMessage({ id, defaultMessage }, values);

  const buttonsUsePage = roles.surface.inverse.fill === palette.page;
  const pageOnSurface = intl.formatNumber(contrastRatio(palette.page, palette.surface), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const accentReplaced = roles.surface.accent !== palette.accent;

  return (
    <div className="space-y-3">
      <div className="bg-page flex gap-5 rounded-md p-4 ring-1 ring-ink/10" aria-hidden="true" inert>
        <nav className="hidden w-24 shrink-0 flex-col gap-2 pt-1 text-smycles-label tracking-[0.2em] @sm:flex">
          <span className="text-ink">• SAVE{/* i18n-allow */}</span>
          <span className="text-ink-muted">GROW{/* i18n-allow */}</span>
          <span className="text-ink-muted">SHARE{/* i18n-allow */}</span>
          <span className="text-ink-muted">MATCH{/* i18n-allow */}</span>
        </nav>
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" size="compact">
              {t("adminSettings.appearance.preview.pageAction", "Edit visibility")}
            </Button>
            <Button variant="secondary" size="compact">
              {t("adminSettings.appearance.preview.pageSecondary", "Organisational record")}
            </Button>
          </div>
          <div className="box bg-surface space-y-3 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-smycles-title">{t("adminSettings.appearance.preview.cardTitle", "Business card")}</span>
              <SegmentedControl<"personal" | "company">
                size="sm"
                value="personal"
                // the preview is inert, the selected state is what it shows
                onChange={() => undefined}
                options={[
                  { value: "personal", label: t("adminSettings.appearance.preview.tabPersonal", "Personal") },
                  { value: "company", label: t("adminSettings.appearance.preview.tabCompany", "Company") },
                ]}
              />
            </div>
            <div className="smycles-field-label">{t("adminSettings.appearance.preview.fieldLabel", "Nationality")}</div>
            <TextInput placeholder={t("adminSettings.appearance.preview.placeholder", "e.g. German")} readOnly />
            <p className="text-smycles-meta text-ink-muted">
              {t("adminSettings.appearance.preview.cardText", "Secondary text, metadata and placeholders stay readable on every palette.")}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="primary" size="compact">{t("adminSettings.appearance.preview.open", "Open ↗")}</Button>
              <Button variant="secondary" size="compact">{t("adminSettings.appearance.preview.share", "↗ Share")}</Button>
              <span className="rounded-sm bg-accent/10 px-2 py-0.5 text-smycles-label text-ink-accent">
                {t("adminSettings.appearance.preview.chip", "New")}
              </span>
            </div>
            <div className="flex h-10 items-end gap-1.5">
              {[55, 80, 40, 95, 65].map((height, index) => (
                <span key={index} className={`w-2.5 ${index === 3 ? "bg-accent-decoration" : "bg-ink-strong"}`} style={{ height: `${height}%` }} />
              ))}
              <span className="ml-2 self-center text-smycles-meta text-ink-muted">
                {t("adminSettings.appearance.preview.bars", "Power bars, the selected one in the accent")}
              </span>
            </div>
          </div>
          <div className="smycles-toast w-fit">
            <span>{t("adminSettings.appearance.preview.toast", "Unsaved changes")}</span>
            <Button variant="primary" size="compact">{t("adminSettings.appearance.preview.save", "Save")}</Button>
          </div>
        </div>
      </div>
      <p className="text-smycles-meta text-ink-muted">
        {buttonsUsePage
          ? t("adminSettings.appearance.preview.hintButtonsPage", "Buttons on boxes use the background colour, because it stands out enough from the boxes.")
          : t(
              "adminSettings.appearance.preview.hintButtonsInk",
              "Buttons on boxes are {colour}: background and boxes differ by only {ratio}:1, buttons in the background colour need 4.5:1.",
              {
                colour: roles.surface.inverse.fill === "#000000"
                  ? t("adminSettings.appearance.preview.black", "black")
                  : t("adminSettings.appearance.preview.white", "white"),
                ratio: pageOnSurface,
              },
            )}
        {accentReplaced
          ? " " + t("adminSettings.appearance.preview.hintAccent", "As text on boxes, the accent is replaced by the ink where it would not be readable.")
          : ""}
      </p>
    </div>
  );
}
